import { type BuilderTheme } from "@/lib/builder-theme";
import { translateHtmlText } from "@/lib/i18n-dict";
import { defaultMenuItems, menuItemsForLang, type MenuItem } from "@/lib/navigation";

export type LayoutTemplateOptions = {
  theme: BuilderTheme;
  menuItems?: MenuItem[];
  page?: string;
};

export function getLayoutLanguage(page = "/"): "zh" | "en" {
  return page === "/en" || page.startsWith("/en/") ? "en" : "zh";
}

export function buildLayoutMenuHtml({
  menuItems = defaultMenuItems,
  page = "/",
}: Pick<LayoutTemplateOptions, "menuItems" | "page">) {
  const lang = getLayoutLanguage(page);
  return menuItemsForLang(menuItems, lang)
    .map((item) => {
      const activeClass = isActiveNavItem(item.href, page) ? " active" : "";
      if (item.children?.length) {
        const childLinks = item.children
          .map((child) => {
            const childActiveClass = isActiveNavItem(child.href, page) ? " active" : "";
            return `<a class="nav-sub-link${childActiveClass}" href="${child.href}" data-builder-field="navLink">${escapeHtml(child.label)}</a>`;
          })
          .join("");
        return `<div class="nav-item has-submenu"><a class="nav-link${activeClass}" href="${item.href}" data-builder-field="navLink">${escapeHtml(item.label)}<span class="nav-caret">▾</span></a><div class="nav-submenu">${childLinks}</div></div>`;
      }
      return `<a class="nav-link${activeClass}" href="${item.href}" data-builder-field="navLink">${escapeHtml(item.label)}</a>`;
    })
    .join("");
}

export function buildLayoutHeaderHtml({
  theme,
  menuItems = defaultMenuItems,
  page = "/",
}: LayoutTemplateOptions): string {
  if (theme.showHeader === false) return "";

  const layout = theme.headerLayout || "left";
  const style = theme.headerStyle || "standard";
  const brand = theme.brandName || "RonnieTech";
  const logo = theme.brandLogo || "/logo.png";
  const headerClass = `topbar header-${style} layout-${layout}`;
  const lang = getLayoutLanguage(page);
  const languageHref = lang === "zh" ? "/en" : "/";
  const languageLabel = lang === "zh" ? "English" : "中文";
  const navLinksHtml = buildLayoutMenuHtml({ menuItems, page });
  const brandHtml = `<a class="brand" href="${lang === "zh" ? "/" : "/en"}" data-builder-field="brand"><img src="${escapeAttribute(logo)}" alt="${escapeAttribute(brand)}" data-builder-field="brandLogo" /></a>`;
  const utilityLinksHtml = `
      <a class="admin-entry" href="${languageHref}" data-builder-field="navLink">${languageLabel}</a>`;

  if (theme.headerHtml) {
    return syncSavedHeaderHtml(theme.headerHtml, {
      headerClass,
      brandHtml,
      navLinksHtml,
      utilityLinksHtml,
      languageHref,
      languageLabel,
    });
  }

  if (layout === "centered") {
    return `<header class="${headerClass}" data-builder-region="header">
  <div class="nav">
    <div class="nav-left">${navLinksHtml}</div>
    ${brandHtml}
    <div class="nav-right">
      ${utilityLinksHtml}
    </div>
  </div>
</header>`;
  }

  if (layout === "compact") {
    return `<header class="${headerClass}" data-builder-region="header">
  <div class="nav">
    ${brandHtml}
    <button class="nav-toggle" data-builder-field="navToggle">☰</button>
    <div class="nav-dropdown">
      ${navLinksHtml}
      ${utilityLinksHtml}
    </div>
  </div>
</header>`;
  }

  return `<header class="${headerClass}" data-builder-region="header">
  <div class="nav">
    ${brandHtml}
    <nav class="nav-links">${navLinksHtml}</nav>
    ${utilityLinksHtml}
  </div>
</header>`;
}

function syncSavedHeaderHtml(
  html: string,
  replacement: {
    headerClass: string;
    brandHtml: string;
    navLinksHtml: string;
    utilityLinksHtml: string;
    languageHref: string;
    languageLabel: string;
  },
) {
  let nextHtml = html;

  nextHtml = nextHtml.replace(/<header\b([^>]*)class="[^"]*"([^>]*)>/i, `<header$1class="${replacement.headerClass}"$2>`);
  if (!/<header\b[^>]*class=/i.test(nextHtml)) {
    nextHtml = nextHtml.replace(/<header\b([^>]*)>/i, `<header$1 class="${replacement.headerClass}">`);
  }

  nextHtml = nextHtml.replace(/<nav class="nav-links"[^>]*>[\s\S]*?<\/nav>/i, `<nav class="nav-links">${replacement.navLinksHtml}</nav>`);
  nextHtml = nextHtml.replace(/<a[^>]*class="[^"]*\bbrand\b[^"]*"[^>]*>[\s\S]*?<\/a>/i, replacement.brandHtml);

  if (/class="[^"]*admin-entry/.test(nextHtml)) {
    nextHtml = nextHtml.replace(/(?:\s*<a[^>]*class="[^"]*admin-entry[^"]*"[^>]*>[\s\S]*?<\/a>)+/i, replacement.utilityLinksHtml);
  } else {
    nextHtml = nextHtml.replace(/(<\/div>\s*<\/header>\s*)$/i, `${replacement.utilityLinksHtml}$1`);
  }

  return nextHtml;
}

function normalizePath(path: string) {
  if (path !== "/" && path.endsWith("/")) return path.slice(0, -1);
  return path;
}

function isActiveNavItem(href: string, page: string) {
  const normalizedHref = normalizePath(href);
  const normalizedPage = normalizePath(page);
  if (normalizedHref === normalizedPage) return true;
  if (normalizedHref === "/" || normalizedHref === "/en") return false;
  return normalizedPage.startsWith(`${normalizedHref}/`);
}

export function buildLayoutFooterHtml({ theme, page = "/" }: Pick<LayoutTemplateOptions, "theme" | "page">): string {
  const lang = getLayoutLanguage(page);
  const quickLinksLabel = lang === "zh" ? "快捷链接" : "Quick Links";
  const rightsLabel = lang === "zh" ? "保留所有权利。" : "All rights reserved.";
  const style = theme.footerStyle || "standard";
  const align = theme.footerAlign || "left";
  const brand = theme.brandName || "RonnieTech";
  const desc = theme.footerDescription || "";
  const footerClass = `footer footer-${style} align-${align}`;
  if (theme.footerHtml) return localizeSavedFooterHtml(theme.footerHtml, lang, footerClass);
  const year = new Date().getFullYear();
  const linksHtml = (theme.footerLinks || [])
    .map((link) => `<a href="${escapeAttribute(link.href)}" data-builder-field="footerLink">${escapeHtml(link.label)}</a>`)
    .join("");
  const columnsHtml = (theme.footerColumns || [])
    .map((col) => {
      const colLinksHtml = (col.links || [])
        .map((link) => `<a href="${escapeAttribute(link.href)}" data-builder-field="footerColumnLink">${escapeHtml(link.label)}</a>`)
        .join("");
      return `<div data-builder-field="footerColumn"><h4>${escapeHtml(col.title)}</h4><div class="footer-column-links">${colLinksHtml}</div></div>`;
    })
    .join("");

  if (style === "minimal") {
    return `<footer class="${footerClass}" data-builder-region="footer">
  <div class="container footer-minimal-content">
    <p>© ${year} <span data-builder-field="brandName">${escapeHtml(brand)}</span>. ${rightsLabel}</p>
  </div>
</footer>`;
  }

  if (style === "bordered") {
    return `<footer class="${footerClass}" data-builder-region="footer">
  <div class="container footer-bordered-content">
    <div class="footer-bordered-top">
      <div>
        <h3 data-builder-field="brandName">${escapeHtml(brand)}</h3>
        <p data-builder-field="footerDescription">${escapeHtml(desc)}</p>
        ${footerSocialSlotHtml()}
      </div>
      ${linksHtml ? `<div class="footer-quick-links">${linksHtml}</div>` : ""}
    </div>
    ${columnsHtml ? `<div class="footer-bordered-columns">${columnsHtml}</div>` : ""}
    <div class="footer-bordered-bottom">
      <p>© ${year} ${escapeHtml(brand)}. ${rightsLabel}</p>
    </div>
  </div>
</footer>`;
  }

  if (style === "split") {
    return `<footer class="${footerClass}" data-builder-region="footer">
  <div class="container footer-split">
    <div class="footer-split-left">
      <h3 data-builder-field="brandName">${escapeHtml(brand)}</h3>
      <p data-builder-field="footerDescription">${escapeHtml(desc)}</p>
      ${footerSocialSlotHtml()}
      <p>© ${year} ${escapeHtml(brand)}</p>
    </div>
    <div class="footer-split-right">
      ${linksHtml ? `<div class="footer-quick-links">${linksHtml}</div>` : ""}
      ${columnsHtml ? `<div class="footer-split-columns">${columnsHtml}</div>` : ""}
    </div>
  </div>
</footer>`;
  }

  if (style === "columns") {
    return `<footer class="${footerClass}" data-builder-region="footer">
  <div class="container footer-columns-layout">
    <div class="footer-col-brand">
      <h3 data-builder-field="brandName">${escapeHtml(brand)}</h3>
      <p data-builder-field="footerDescription">${escapeHtml(desc)}</p>
      ${footerSocialSlotHtml()}
    </div>
    ${linksHtml ? `<div class="footer-col-links"><h4>${quickLinksLabel}</h4>${linksHtml}</div>` : ""}
    ${columnsHtml ? `<div class="footer-col-columns">${columnsHtml}</div>` : ""}
  </div>
  <div class="footer-bottom">
    <div class="container">
      <p>© ${year} ${escapeHtml(brand)}</p>
    </div>
  </div>
</footer>`;
  }

  if (style === "centered") {
    return `<footer class="${footerClass}" data-builder-region="footer">
  <div class="container footer-centered-content">
    <h3 data-builder-field="brandName">${escapeHtml(brand)}</h3>
    <p class="footer-desc-centered" data-builder-field="footerDescription">${escapeHtml(desc)}</p>
    ${footerSocialSlotHtml()}
    ${linksHtml ? `<div class="footer-quick-links-centered">${linksHtml}</div>` : ""}
    ${columnsHtml ? `<div class="footer-columns-centered">${columnsHtml}</div>` : ""}
    <p class="copyright">© ${year} ${escapeHtml(brand)}</p>
  </div>
</footer>`;
  }

  return `<footer class="${footerClass}" data-builder-region="footer">
  <div class="container footer-grid">
    <div>
      <h3 data-builder-field="brandName">${escapeHtml(brand)}</h3>
      <p data-builder-field="footerDescription">${escapeHtml(desc)}</p>
      ${footerSocialSlotHtml()}
    </div>
    ${linksHtml ? `<div><h4>${quickLinksLabel}</h4><div class="footer-quick-links">${linksHtml}</div></div>` : ""}
    ${columnsHtml}
  </div>
  <div class="footer-bottom">
    <div class="container">
      <p>© ${year} ${escapeHtml(brand)}</p>
    </div>
  </div>
</footer>`;
}

function localizeSavedFooterHtml(html: string, lang: "zh" | "en", footerClass: string) {
  html = normalizeSavedFooterHtml(html, footerClass);

  if (lang === "en") return translateHtmlText(html, "en");

  return html
    .replaceAll("Industrial fastener solutions, engineering selection support, cross reference, custom sourcing and RFQ services.", "工业紧固件解决方案、工程选型支持、标准替代、非标寻源和询价服务。")
    .replaceAll("Portal", "门户栏目")
    .replaceAll("About Us", "关于我们")
    .replaceAll("Capabilities", "能力中心")
    .replaceAll("Industries", "行业应用")
    .replaceAll("Tools", "工具入口")
    .replaceAll("Product Finder", "产品检索")
    .replaceAll("product检索", "产品检索")
    .replaceAll("Cross Reference", "标准替代")
    .replaceAll("Standard Substitution", "标准替代")
    .replaceAll("Drawing RFQ", "图纸询价")
    .replaceAll("DrawingRFQ", "图纸询价")
    .replaceAll("Support", "服务支持")
    .replaceAll("Engineering Review", "工程评估")
    .replaceAll("Quality Documents", "质量文件")
    .replaceAll("quality documents", "质量文件")
    .replaceAll("Procurement Support", "采购支持")
    .replaceAll("采购Support", "采购支持")
    .replaceAll("Quick Links", "快捷链接")
    .replaceAll("All rights reserved.", "保留所有权利。");
}

function normalizeSavedFooterHtml(html: string, footerClass: string) {
  let nextHtml = html
    .replace(/\s*<section\b[^>]*class="[^"]*\brt-section\b[^"]*"[^>]*>[\s\S]*?data-rt-dynamic="social-links"[\s\S]*?<\/section>/gi, "")
    .replace(/<article\b[^>]*id="i[a-z0-9]+"[^>]*>\s*<\/article>/gi, "")
    .replace(/<div\b[^>]*class="[^"]*\bdb-social\b[^"]*"[^>]*data-rt-dynamic="social-links"[^>]*>[\s\S]*?<\/div>/gi, footerSocialSlotHtml())
    .replace(/<div\b[^>]*data-rt-dynamic="social-links"[^>]*class="[^"]*\bdb-social\b[^"]*"[^>]*>[\s\S]*?<\/div>/gi, footerSocialSlotHtml());

  nextHtml = nextHtml.replace(/<footer\b([^>]*)class="[^"]*"([^>]*)>/i, `<footer$1class="${footerClass}"$2>`);
  if (!/<footer\b[^>]*class=/i.test(nextHtml)) {
    nextHtml = nextHtml.replace(/<footer\b([^>]*)>/i, `<footer$1 class="${footerClass}">`);
  }

  if (!/data-rt-dynamic="social-links"/i.test(nextHtml)) {
    nextHtml = nextHtml.replace(
      /(<div\b[^>]*class="[^"]*\bfooter-col-brand\b[^"]*"[^>]*>[\s\S]*?<p\b[^>]*data-builder-field="footerDescription"[^>]*>[\s\S]*?<\/p>)/i,
      `$1${footerSocialSlotHtml()}`,
    );
  }

  return nextHtml;
}

function footerSocialSlotHtml() {
  return `<div class="footer-social-slot" data-rt-dynamic="social-links"></div>`;
}

export function buildLayoutShellHtml({
  theme,
  menuItems = defaultMenuItems,
  page = "/",
  contentHtml,
}: LayoutTemplateOptions & { contentHtml: string }) {
  return `<main data-builder-region="content">
${contentHtml}
</main>`;
}

export function buildThemeVariableCss(theme: BuilderTheme) {
  return `:root {
  --rt-site-layout: ${theme.layout};
  --rt-header-height: ${theme.headerHeight}px;
  --rt-logo-width: ${theme.logoWidth}px;
  --rt-logo-max-height: ${theme.logoMaxHeight}px;
  --rt-nav-font-size: ${theme.navFontSize}px;
  --rt-nav-gap: ${theme.navGap}px;
  --rt-nav-background: ${theme.navBackground};
  --rt-footer-background: ${theme.footerBackground};
  --rt-footer-padding-top: ${theme.footerPaddingTop}px;
  --rt-footer-padding-bottom: ${theme.footerPaddingBottom}px;
  --rt-footer-column-count: ${theme.footerColumnCount};
  --rt-footer-column-gap: ${theme.footerColumnGap}px;
}`;
}

export function escapeHtml(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export function escapeAttribute(value: string) {
  return escapeHtml(value).replaceAll("'", "&#39;");
}
